import _ from "lodash";

import LiquidTemplate from "@stw/liquid-template";
import { stripFonts } from "@stw/unicode-fonts";

type ScriptTextValidator = {
  message: string;
  regex?: RegExp;
  oneOrMore?: string[];
};

const BANNED_PUBLIC_LINK_SHORTENERS = [
  "9qr.de",
  "alturl.com",
  "app.link",
  "bc.vc",
  "bit.do",
  "bit.ly",
  "bitly.com",
  "bitly.ws",
  "bl.ink",
  "budurl.com",
  "clicky.me",
  "cutt.ly",
  "goo.gl",
  "is.gd",
  "lc.chat",
  "linki.la",
  "lnkd.in",
  "minm.xyz",
  "ow.ly",
  "rb.gy",
  "rebrand.ly",
  "s2r.co",
  "serveirc.com",
  "shorturl.at",
  "shrtco.de",
  "soo.gd",
  "t.co",
  "t.ly",
  "t2m.io",
  "tinurl.com",
  "tiny.cc",
  "tinyurl.com",
  "yourls.org",
];

const bannedLinkShortenerRegex = new RegExp(
  BANNED_PUBLIC_LINK_SHORTENERS.map(
    domain => `\\b${_.escapeRegExp(domain)}\\b`
  ).join("|"),
  "im"
);

export const scriptLinkValidations: ScriptTextValidator[] = [
  {
    message:
      "Insecure links compromise deliverability. Replace http:// with https:// and then retest the links.",
    regex: /http:\/\//im,
  },
  {
    message:
      "URL shorteners compromise deliverability. Use the full URL or a link shortener on a custom domain.",
    regex: bannedLinkShortenerRegex,
  },
  {
    message:
      "Google Docs links compromise deliverability. Use a web page hosted on your domain instead.",
    regex: /docs\.google\.com/im,
  },
];

export function hasOptOutLanguage(
  script: string,
  { optOutPhrases }: { optOutPhrases: string[] }
): boolean {
  return optOutPhrases.some(phrase =>
    stripFonts(script).toLowerCase()?.includes(phrase.toLowerCase().trim())
  );
}

export default function validateMessageScript(
  script: string,
  {
    validVariables,
    validateLinks,
    requireOptOutLanguage,
    optOutPhrases,
  }: {
    validVariables: Set<string> | false;
    validateLinks: boolean;
  } & (
    | {
        requireOptOutLanguage: true;
        optOutPhrases: string[];
      }
    | { requireOptOutLanguage: false; optOutPhrases?: string[] | null }
  )
): { ok: true; template: LiquidTemplate } | { ok: false; error: string } {
  const renderResult = LiquidTemplate.fromString(script);
  if (!renderResult.ok) {
    return { ok: false, error: `Invalid script: ${renderResult.error}` };
  }

  if (validVariables) {
    const variablesInScript = renderResult.template.getRequiredVariables();
    const invalidVariables = variablesInScript.filter(
      v => !validVariables.has(v)
    );
    if (invalidVariables.length > 0) {
      return {
        ok: false,
        error: `Script contains invalid variable(s): ${invalidVariables.join(
          ", "
        )}`,
      };
    }
  }

  if (validateLinks) {
    const linkError = findFirstError(script, scriptLinkValidations);
    if (linkError) {
      return {
        ok: false,
        error: `Script contains invalid link(s). ${linkError}`,
      };
    }
  }

  if (requireOptOutLanguage) {
    if (!hasOptOutLanguage(script, { optOutPhrases })) {
      return {
        ok: false,
        error: "Missing instructions to reply with valid opt out language.",
      };
    }
  }

  return { ok: true, template: renderResult.template };
}

export function findFirstError(
  text: string,
  validators: ScriptTextValidator[]
): string | undefined {
  return (
    validators.find(({ regex, oneOrMore }) => {
      if (regex) {
        return text.match(regex);
      }
      if (oneOrMore) {
        return !oneOrMore.some(phrase =>
          text?.toLowerCase()?.includes(phrase.toLowerCase().trim())
        );
      }
    })?.message ?? undefined
  );
}

// should be the same as MessagingStrategyName
type MessagingStrategy =
  | "FAKE"
  | "FAKE_POOL"
  | "BANDWIDTH_TOLL_FREE"
  | "BANDWIDTH_10_DLC"
  | "BANDWIDTH_10_DLC_POOL"
  | "BANDWIDTH_SHORT_CODE"
  | "BANDWIDTH_UNREGISTERED_POOL"
  | "TWILIO_SHORT_CODE"
  | "FAKE_SHORT_CODE"
  | "FAKE_10_DLC"
  | "FAKE_TOLL_FREE"
  | "SINCH_10_DLC";

const OPT_OUT_LANGUAGE_EXEMPT_MESSAGING_STRATEGIES = new Set<MessagingStrategy>(
  ["BANDWIDTH_SHORT_CODE", "TWILIO_SHORT_CODE", "FAKE_SHORT_CODE"]
);

export function messagingStrategyRequiresOptOutLanguage(
  messagingStrategy: MessagingStrategy
): boolean {
  return !OPT_OUT_LANGUAGE_EXEMPT_MESSAGING_STRATEGIES.has(messagingStrategy);
}
